import { useMemo } from "react";
import { solesDm } from "../lib/money";
import type { TipoProducto } from "@huayruro/shared";
import { agruparPorPrincipio } from "../lib/agruparBusqueda";
import { aProductoVenta, type ResultadoBusqueda } from "../lib/useCatalogoVenta";
import type { ProductoVenta } from "../lib/tipos";

type Props = {
  buscar: (q: string) => ResultadoBusqueda[];
  onAgregar: (pv: ProductoVenta) => void;
  /** El texto del buscador vive en el Mostrador: «Faltante» lo reusa sin volver a tipearlo. */
  query: string;
  onQuery: (q: string) => void;
  /** Cuando la búsqueda no encuentra nada (RU-1, tramo 07), tres salidas: el producto está en la mano
   *  («Sí lo tengo»), no hay («No hay» = el faltante de siempre) o el Encargado lo crea ya. */
  onSiLoTengo?: () => void;
  onAnotarFaltante?: () => void;
  /** Solo para quien puede crear la ficha (el Encargado): abre Catálogo con lo buscado puesto. */
  onCrearProducto?: (() => void) | undefined;
};

// Búsqueda local (Dexie + GTIN) con tolerancia (sin tildes ni espacios, por cómo suena, una letra de diferencia).
//
// Forma C (Kevin, 16-oct-2026): agrupada por principio activo, con las líneas de la B — (1) el nombre, con el
// color de su tipo; (2) laboratorio, composición y qué hace; (3) dónde está y cuánto queda. Sin botones grandes:
// TOCAR EL PRODUCTO agrega la unidad (o su presentación más chica); el blíster y la caja tienen su precio a un
// toque, aparte. Fondo suave por tipo: marca azul, genérico verde, aseo morado, galénico durazno.
// «Corregir precio» salió del Mostrador (Kevin: «no es lo normal»): el precio se corrige en Catálogo.
//
// Candado del e2e: el PRIMER botón de cada resultado es la presentación base con su precio
// (`apps/e2e/util/acciones.ts`, agregarAlCarrito). Nada se pone antes de ese botón.
export function Buscador({
  buscar,
  onAgregar,
  query,
  onQuery,
  onSiLoTengo,
  onAnotarFaltante,
  onCrearProducto,
}: Props) {
  const resultados = useMemo(() => buscar(query), [buscar, query]);
  const grupos = useMemo(() => agruparPorPrincipio(resultados), [resultados]);

  return (
    <section className="bg-card border border-line rounded-[12px] flex flex-col md:h-full md:min-h-0">
      <div className="p-3 border-b border-line">
        <input
          type="search"
          placeholder="Buscar por nombre o código..."
          title="Busca por nombre, código o principio activo: debajo salen las otras marcas y los genéricos"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          className="w-full min-h-[44px] px-3 py-2 rounded-[9px] bg-field border border-line-input focus:border-ok outline-none text-ink"
        />
        <p className="mt-1 text-xs text-ink-2">
          O escanea un código de barras (auto-detecta el lector).
        </p>
      </div>

      {/* R-C: la lista tiene su propio desplazamiento. En el celular lleva tope de alto para que la
          venta en curso quede cerca; desde 768 px ocupa la columna y se desplaza por dentro. */}
      <div className="overflow-y-auto max-md:max-h-[46vh] md:flex-1">
        {resultados.length === 0 ? (
          <ul>
            <li className="p-6 text-center text-ink-3 text-sm">
              {/* «Sin resultados.» se conserva letra por letra: lo busca el e2e (01-venta.spec.ts). */}
              <p>Sin resultados.</p>
              {query.trim() && (onSiLoTengo || onAnotarFaltante || onCrearProducto) && (
                <>
                  <p className="mt-2 text-[12.5px] text-ink-2">¿Lo tienes en la mano?</p>
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {onSiLoTengo && (
                      <button
                        type="button"
                        onClick={onSiLoTengo}
                        className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-[9px] border border-tip-border bg-ok-soft px-4 text-[13.5px] font-semibold text-ok hover:bg-tip-hover"
                      >
                        Sí lo tengo
                      </button>
                    )}
                    {onAnotarFaltante && (
                      <button
                        type="button"
                        onClick={onAnotarFaltante}
                        className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-[9px] border border-warn-dot/50 bg-card px-4 text-[13.5px] font-semibold text-warn hover:bg-warn-soft"
                      >
                        No hay
                      </button>
                    )}
                    {onCrearProducto && (
                      <button
                        type="button"
                        onClick={onCrearProducto}
                        className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-[9px] border border-line-input bg-card px-4 text-[13.5px] font-semibold text-ink-emph hover:bg-hover-btn"
                      >
                        Crear producto
                      </button>
                    )}
                  </div>
                </>
              )}
            </li>
          </ul>
        ) : (
          // Cada grupo es un bloque con su título y SU lista. El título NO es una fila (`li`): en el catálogo el
          // principio activo suele llamarse igual que el producto («Paracetamol 500 mg»), y el e2e busca la fila
          // del producto por su nombre (16-oct-2026: con el título como `li`, la encontraba a ella y no al producto).
          grupos.map((g) => (
            <section
              key={`g-${g.clave}`}
              data-testid="grupo-principio"
              aria-label={g.principio ?? "Sin principio activo"}
            >
              <p className="sticky top-0 z-[1] border-t border-line bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-2">
                {g.principio ? (
                  // Solo la primera letra en mayúscula: con `capitalize` salía «Paracetamol 500 Mg».
                  <span>{g.principio.charAt(0).toUpperCase() + g.principio.slice(1).toLowerCase()}</span>
                ) : (
                  "Sin principio activo"
                )}
                <span className="font-normal text-ink-3"> · {resumenGrupo(g.items)}</span>
              </p>
              <ul>
                {g.items.map((r) => (
                  <FilaProducto key={r.producto_id} r={r} onAgregar={onAgregar} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </section>
  );
}

const ESTILO_TIPO: Record<TipoProducto, { fondo: string; nombre: string }> = {
  marca: { fondo: "bg-tipo-marca", nombre: "text-tipo-marca-ink" },
  generico: { fondo: "bg-tipo-generico", nombre: "text-tipo-generico-ink" },
  aseo: { fondo: "bg-tipo-aseo", nombre: "text-tipo-aseo-ink" },
  galenico: { fondo: "bg-tipo-galenico", nombre: "text-tipo-galenico-ink" },
};

function resumenGrupo(items: ResultadoBusqueda[]): string {
  const n: Record<TipoProducto, number> = { marca: 0, generico: 0, aseo: 0, galenico: 0 };
  for (const r of items) n[r.tipo]++;
  const plural: Record<TipoProducto, [string, string]> = {
    marca: ["marca", "marcas"],
    generico: ["genérico", "genéricos"],
    galenico: ["galénico", "galénicos"],
    aseo: ["de aseo", "de aseo"],
  };
  return (["marca", "generico", "galenico", "aseo"] as const)
    .filter((t) => n[t] > 0)
    .map((t) => `${n[t]} ${plural[t][n[t] === 1 ? 0 : 1]}`)
    .join(" · ");
}

const IconoUbicacion = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
    className="shrink-0"
  >
    <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);
const IconoAlerta = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
    className="shrink-0"
  >
    <path d="M12 3 2 21h20L12 3z" />
    <path d="M12 10v5M12 18h.01" />
  </svg>
);

function FilaProducto({
  r,
  onAgregar,
}: {
  r: ResultadoBusqueda;
  onAgregar: (pv: ProductoVenta) => void;
}) {
  const pres = [...r.presentaciones].sort((a, b) => a.factor - b.factor);
  const base = pres.find((p) => p.es_base) ?? pres[0]!;
  const otras = pres.filter((p) => p !== base);
  const estilo = ESTILO_TIPO[r.tipo];
  const unidad = r.por_unidad ? "und." : "env.";
  const linea2 = [r.laboratorio, r.principio_activo?.toLowerCase(), r.accion]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className={`relative border-t border-line-row ${estilo.fondo}`} data-tipo={r.tipo}>
      {/* Toda la fila es el botón de la presentación base: tocar el producto lo agrega. */}
      <button
        type="button"
        onClick={() => onAgregar(aProductoVenta(r, base, null))}
        className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-3 py-2 text-left hover:brightness-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ok"
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className={`text-[14.5px] font-semibold leading-snug ${estilo.nombre}`}>
            {r.nombre}
            {r.presentacion_texto && (
              <span className="text-[12px] font-normal text-ink-2"> {r.presentacion_texto}</span>
            )}
            {r.coincidencia && (
              <span className="ml-1.5 whitespace-nowrap rounded-[5px] bg-warn-soft px-1.5 py-px align-middle text-[10.5px] font-medium text-warn">
                {r.coincidencia === "sonido" ? "suena parecido" : "1 letra distinta"}
              </span>
            )}
          </span>
          {linea2 && <span className="text-[12px] leading-snug text-ink-2">{linea2}</span>}
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-ink-2">
            {r.ubicacion && (
              <span className="inline-flex items-center gap-1">
                <IconoUbicacion />
                {r.ubicacion}
              </span>
            )}
            {r.stock_cache != null &&
              (r.stock_cache <= 0 ? (
                <span className="font-semibold text-accent-ink">Sin stock</span>
              ) : r.quedan_pocas ? (
                <span
                  className="inline-flex items-center gap-1 font-semibold text-warn"
                  title="Quedan pocas en esta botica"
                >
                  <IconoAlerta />
                  quedan {r.stock_cache} {unidad}
                </span>
              ) : (
                <span className="tabular-nums">
                  {r.stock_cache} {unidad}
                </span>
              ))}
          </span>
        </span>
        <span className="flex flex-col items-end">
          <span className="font-mono text-[16px] font-bold leading-tight text-ok tabular-nums">
            {solesDm(base.precio_total_dm)}
          </span>
          <span className="text-[11px] text-ink-3">{base.presentacion_nombre}</span>
        </span>
      </button>
      {/* Blíster y caja en su propia fila, debajo (no se puede poner un botón dentro de otro). Montados encima
          de la fila tapaban el laboratorio y el stock a 390 px (captura del 16-oct-2026). */}
      {otras.length > 0 && (
        <div className="-mt-1 flex flex-wrap justify-end gap-1 px-3 pb-2">
          {otras.map((p) => (
            <button
              key={p.presentacion_id}
              type="button"
              onClick={() => onAgregar(aProductoVenta(r, p, null))}
              className="min-h-[36px] whitespace-nowrap rounded-[7px] border border-line-input bg-card px-2 text-[11.5px] text-ink hover:border-ok hover:bg-ok-soft md:min-h-[30px]"
            >
              {p.presentacion_nombre}{" "}
              <span className="font-mono font-semibold text-ok tabular-nums">
                {solesDm(p.precio_total_dm)}
              </span>
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
